import React, { useEffect, useState, useRef } from 'react';
import { DomainCheckoutData } from '../../pages/checkout/DomainCheckout';
import { domainService } from '../../services/domainService';

interface DomainConfirmationStepProps {
  data: DomainCheckoutData;
}

type ProvStatus =
  | 'unknown'         // never queried yet
  | 'not_started'     // backend has no attempt row for this order (legacy or pre-P2)
  | 'pending'
  | 'in_progress'     // submitted to AWS, waiting on operation to settle
  | 'completed'       // AWS confirmed registration
  | 'failed'
  | 'awaiting_admin'; // hold-for-human case

interface ProvData {
  status: ProvStatus;
  domain_name?: string;
  aws_operation_id?: string;
  aws_operation_status?: string;
  last_error?: string | null;
  completed_at?: string | null;
  domain?: any;
}

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes — match server's fast-phase budget

const DomainConfirmationStep: React.FC<DomainConfirmationStepProps> = ({ data }) => {
  const orderId = data.orderId;
  const orderNumber = data.orderNumber || orderId || 'ORDER-PENDING';

  const [prov, setProv] = useState<ProvData>({ status: 'unknown' });
  const [pollError, setPollError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef<number>(Date.now());

  // Poll provisioning status until it reaches a terminal state.
  useEffect(() => {
    if (!orderId) {
      // We don't have an order ID to poll on; degrade to legacy display.
      setProv({ status: 'not_started' });
      return;
    }

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;

      // Timeout safeguard so the page doesn't poll forever.
      if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
        setPollError(
          'Registration is taking longer than usual. AWS is still processing it; you will receive an email once complete. ' +
          'You can also check your domains list later.'
        );
        return;
      }

      try {
        const resp = await domainService.getProvisioningStatus(orderId);
        if (cancelled) return;

        if (resp.success && resp.data) {
          const d = resp.data as ProvData;
          setProv(d);

          // Terminal states — stop polling.
          if (d.status === 'completed' || d.status === 'failed' || d.status === 'awaiting_admin') {
            return;
          }
        } else if (!resp.success) {
          // Don't surface transient errors; just keep polling.
          console.warn('Provisioning poll: backend returned error', resp.error);
        }
      } catch (e: any) {
        console.warn('Provisioning poll error:', e?.message);
      }

      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    // Fire first poll immediately.
    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [orderId]);

  // Decide visual state purely from `prov.status`.
  const isInProgress = prov.status === 'pending' || prov.status === 'in_progress' || prov.status === 'unknown';
  const isCompleted = prov.status === 'completed';
  const isAwaitingAdmin = prov.status === 'awaiting_admin' || prov.status === 'failed';
  const isLegacy = prov.status === 'not_started';

  const headlineColor = isCompleted ? 'from-green-500 to-emerald-500'
    : isAwaitingAdmin ? 'from-amber-500 to-orange-500'
    : 'from-blue-500 to-indigo-500';

  const headline = isCompleted ? 'Domain Registered Successfully!'
    : isAwaitingAdmin ? 'Registration Needs Attention'
    : isLegacy ? 'Order Received'
    : 'Registration in Progress';

  const subline = isCompleted
    ? 'Your domain is now active in our system.'
    : isAwaitingAdmin
    ? "We've received your payment but the registration ran into an issue. Our team has been notified and will follow up shortly."
    : isLegacy
    ? "Thank you for choosing 12and22. Your order is being processed and you'll receive confirmation by email."
    : 'Your payment has been received. AWS is registering the domain — this usually takes 1-10 minutes. This page updates automatically.';

  // Use real domain data once available; otherwise fall back to checkout data.
  const displayDomainName = prov.domain?.domain_name || prov.domain_name || data.domainName;
  const displayExpiration = prov.domain?.expiration_date
    ? new Date(prov.domain.expiration_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>

      <div className="max-w-2xl mx-auto text-center relative z-10">
        {/* Icon */}
        <div className={`mx-auto flex items-center justify-center h-20 w-20 bg-gradient-to-br ${headlineColor} rounded-2xl mb-6 shadow-lg relative overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
          {isCompleted ? (
            <svg className="h-10 w-10 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : isAwaitingAdmin ? (
            <svg className="h-10 w-10 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M4.93 19.07A10 10 0 1119.07 4.93 10 10 0 014.93 19.07z" />
            </svg>
          ) : (
            // Spinner for in-progress
            <div className="animate-spin h-10 w-10 border-4 border-white/40 border-t-white rounded-full relative z-10"></div>
          )}
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-2">{headline}</h2>
        <p className="text-lg text-gray-600 mb-2">{subline}</p>

        {isInProgress && (
          <p className="text-sm text-gray-500 mb-6">
            {prov.aws_operation_status && (
              <>AWS status: <span className="font-mono">{prov.aws_operation_status}</span> · </>
            )}
            Refreshing every {POLL_INTERVAL_MS / 1000}s
          </p>
        )}
        {isAwaitingAdmin && prov.last_error && (
          <div className="mt-2 mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-left text-sm text-amber-900">
            <strong>Details for our team:</strong>
            <p className="mt-1 break-words">{prov.last_error}</p>
          </div>
        )}
        {pollError && (
          <div className="mt-2 mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-left text-sm text-blue-900">
            {pollError}
          </div>
        )}

        {/* Order Details */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h3>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Order ID:</span>
              <span className="font-mono font-medium">{orderNumber}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Domain Name:</span>
              <span className="font-medium">{displayDomainName}</span>
            </div>

            {displayExpiration && (
              <div className="flex justify-between">
                <span className="text-gray-600">Expires:</span>
                <span className="font-medium">{displayExpiration}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">Privacy Protection:</span>
              <span className="font-medium text-green-600">Included</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Auto-Renewal:</span>
              <span className="font-medium text-green-600">Enabled</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Registrar:</span>
              <span className="font-medium">AWS Route 53 via 12and22</span>
            </div>

            <hr className="my-3" />

            <div className="flex justify-between text-lg font-bold">
              <span className="text-gray-900">Total Paid:</span>
              <span className="text-green-600">${data.pricing.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* What Happens Next — content depends on state */}
        <div className={`rounded-lg p-6 mb-8 text-left ${isCompleted ? 'bg-green-50 border border-green-200' : isAwaitingAdmin ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isCompleted ? 'text-green-900' : isAwaitingAdmin ? 'text-amber-900' : 'text-blue-900'}`}>
            {isCompleted ? "What's Next?" : isAwaitingAdmin ? "What's Happening" : 'What Happens Next?'}
          </h3>

          <div className={`space-y-3 ${isCompleted ? 'text-green-800' : isAwaitingAdmin ? 'text-amber-800' : 'text-blue-800'}`}>
            {isCompleted ? (
              <>
                <p>Your domain is now registered with AWS Route 53 and active in your dashboard.</p>
                <p>It may take up to 48 hours for DNS to propagate worldwide before websites and email pointing to this domain become reachable.</p>
                <p>You'll also receive an email confirmation shortly.</p>
              </>
            ) : isAwaitingAdmin ? (
              <>
                <p>Your payment was received successfully, but the automated registration step needs human review.</p>
                <p>This is rare — common causes are a contact-info issue at the registrar or the domain becoming unavailable between checkout and payment.</p>
                <p>Our team has been notified automatically. We'll either complete the registration manually or refund your payment within 1 business day.</p>
              </>
            ) : (
              <>
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-white/70 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">1</span>
                  <div>
                    <div className="font-medium">AWS is registering your domain</div>
                    <div className="text-sm opacity-80">Usually 1-10 minutes. We're polling AWS for you; this page updates automatically.</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-white/70 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">2</span>
                  <div>
                    <div className="font-medium">Confirmation email</div>
                    <div className="text-sm opacity-80">Once AWS confirms, we'll email you with the registration details.</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-white/70 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">3</span>
                  <div>
                    <div className="font-medium">DNS propagation (24-48 hours)</div>
                    <div className="text-sm opacity-80">After registration, DNS changes may take up to 48 hours to reach all networks worldwide.</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="bg-secondary-500 text-white px-8 py-3 rounded-lg hover:bg-secondary-600 transition-colors font-semibold"
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => window.location.href = '/domains'}
            className="border border-secondary-500 text-secondary-500 px-8 py-3 rounded-lg hover:bg-secondary-50 transition-colors font-semibold"
          >
            Register Another Domain
          </button>
        </div>
      </div>
    </div>
  );
};

export default DomainConfirmationStep;
